// Pure helpers for agent templates: {{variable}} tokens, and converting between the API's flat
// rule lists and the "OR groups of AND rows" the editors work with. No React, no I/O.

export const TEMPLATES_ENDPOINT = '/api/v1.0/agenttemplates';

export const RULE_KEYS = ['Subject', 'From', 'To', 'Body'];

export const BEHAVIOR_TYPES = [
	{ value: 0, label: 'Toggle', description: 'Raises and clears automatically based on incoming emails.' },
	{ value: 1, label: 'Heartbeat', description: 'Expects a check-in email within a set interval, and alerts if none arrives.' },
	{ value: 2, label: 'Manual Clear', description: 'Raises on incoming email but can only be cleared manually.' },
];
export const HEARTBEAT_BEHAVIOR = 1;

export const DEFAULT_HEARTBEAT_MINUTES = 60;
export const MAX_HEARTBEAT_MINUTES = 10080;
export const MAX_AUTO_RESET_MINUTES = 1440;
export const DEFAULT_AUTO_RESET_VALUE = 5;

// A fresh regex per call: a shared /g regex keeps lastIndex between uses.
const tokenPattern = () => /\{\{\s*([A-Za-z][A-Za-z0-9_]*)\s*\}\}/g;

// Anything that looks like {{...}} but is not a valid one-word name, e.g. {{server name}} or {{my-var}}.
const looseTokenPattern = () => /\{\{([^{}]*)\}\}/g;
const validTokenExact = /^\{\{\s*[A-Za-z][A-Za-z0-9_]*\s*\}\}$/;

export const VARIABLE_NAME_HINT = 'A variable is one word with no spaces, written in camelCase, e.g. {{serverName}}.';

/** The {{...}} placeholders in `texts` whose name is not a single valid word. */
export const invalidTokens = (texts) => {
	const bad = [];
	for (const text of texts) {
		for (const match of String(text ?? '').matchAll(looseTokenPattern())) {
			if (!validTokenExact.test(match[0]) && !bad.includes(match[0])) bad.push(match[0]);
		}
	}
	return bad;
};

export const containsToken = (text) => tokenPattern().test(text ?? '');

/** Distinct variable keys in `texts`, in first-use order (names are case-insensitive). */
export const extractVariableKeys = (texts) => {
	const found = [];
	for (const text of texts) {
		for (const match of String(text ?? '').matchAll(tokenPattern())) {
			const key = match[1];
			if (!found.some((f) => f.toLowerCase() === key.toLowerCase())) found.push(key);
		}
	}
	return found;
};

export const newRow = () => ({ key: 'Subject', matchType: 'CONTAINS', value: '' });

export const newCondition = () => ({ id: crypto.randomUUID(), statusName: '', groups: [[newRow()]] });

export const emptyForm = () => ({
	name: '',
	description: '',
	agentName: '',
	agentDescription: '',
	behaviorType: 0,
	heartbeatIntervalMinutes: DEFAULT_HEARTBEAT_MINUTES,
	heartbeatMissedStatusName: '',
	autoResetEnabled: false,
	autoResetValue: DEFAULT_AUTO_RESET_VALUE,
	autoResetUnit: 'minutes',
	autoResetToInitialState: false,
	autoResetStatusName: '',
	dismissStatusName: '',
	identifierGroups: [[newRow()]],
	alertConditions: [],
	variables: [],
});

// ── rules: flat API list <-> OR groups of AND rows ───────────────────────────

const toRow = (rule) => ({ key: rule.key, matchType: rule.matchType, value: rule.value });

/** [{key, matchType, value, groupId}] -> [[row, ...], ...] ordered by groupId. */
export const groupsFromRules = (rules = []) => {
	const byGroup = new Map();
	for (const rule of rules) {
		const id = rule.groupId ?? 1;
		byGroup.set(id, [...(byGroup.get(id) ?? []), toRow(rule)]);
	}
	return [...byGroup.entries()].sort((a, b) => a[0] - b[0]).map(([, rows]) => rows);
};

/** The inverse: group N (1-based) becomes groupId N. Rows with no value are dropped. */
export const rulesFromGroups = (groups) => groups.flatMap((rows, index) =>
	rows
		.filter((row) => (row.value ?? '').trim() !== '')
		.map((row) => ({ key: row.key, matchType: row.matchType, value: row.value.trim(), groupId: index + 1 })));

/** Flat alert conditions -> one card per status, each with its own OR groups. */
export const conditionsFromApi = (conditions = []) => {
	const order = [];
	const byStatus = new Map();
	for (const condition of conditions) {
		const name = condition.statusName;
		const existing = order.find((o) => o.toLowerCase() === name.toLowerCase());
		const key = existing ?? name;
		if (!existing) order.push(name);
		byStatus.set(key, [...(byStatus.get(key) ?? []), condition]);
	}
	return order.map((name) => ({
		id: crypto.randomUUID(),
		statusName: name,
		groups: groupsFromRules(byStatus.get(name)),
	}));
};

export const conditionsToApi = (conditions) => conditions.flatMap((condition) =>
	rulesFromGroups(condition.groups).map((rule) => ({ ...rule, statusName: condition.statusName })));

// ── auto-reset: minutes <-> value + unit ─────────────────────────────────────

export const toAutoResetMinutes = (value, unit) => {
	const raw = parseInt(unit === 'hours' ? Number(value) * 60 : value, 10) || 1;
	return Math.min(MAX_AUTO_RESET_MINUTES, Math.max(1, raw));
};

export const fromAutoResetMinutes = (minutes) => {
	if (!minutes || minutes <= 0) return { enabled: false, value: DEFAULT_AUTO_RESET_VALUE, unit: 'minutes' };
	if (minutes % 60 === 0) return { enabled: true, value: minutes / 60, unit: 'hours' };
	return { enabled: true, value: minutes, unit: 'minutes' };
};

// ── variables ────────────────────────────────────────────────────────────────

/** Every text a {{variable}} can appear in: agent name/description and rule values. */
export const templateTexts = (form) => [
	form.agentName,
	form.agentDescription,
	...form.identifierGroups.flat().map((row) => row.value),
	...form.alertConditions.flatMap((c) => c.groups.flat().map((row) => row.value)),
];

/**
 * The variables the form currently uses, keeping the label/example already typed for any that
 * were there before. Variables are derived from the text rather than declared by hand, so a
 * token can never be undeclared and a declared variable can never go unused.
 */
export const deriveVariables = (form, previous = form.variables ?? []) =>
	extractVariableKeys(templateTexts(form)).map((key) => {
		const existing = previous.find((v) => v.key.toLowerCase() === key.toLowerCase());
		return { key, label: existing?.label || key, example: existing?.example ?? '' };
	});

// ── form <-> API ─────────────────────────────────────────────────────────────

export const formFromTemplate = (template) => {
	const definition = template.definition ?? {};
	const autoReset = fromAutoResetMinutes(definition.autoResetMinutes);
	return {
		name: template.name ?? '',
		description: template.description ?? '',
		agentName: definition.agentName ?? '',
		agentDescription: definition.agentDescription ?? '',
		behaviorType: definition.behaviorType ?? 0,
		heartbeatIntervalMinutes: definition.heartbeatIntervalMinutes ?? DEFAULT_HEARTBEAT_MINUTES,
		heartbeatMissedStatusName: definition.heartbeatMissedStatusName ?? '',
		autoResetEnabled: autoReset.enabled,
		autoResetValue: autoReset.value,
		autoResetUnit: autoReset.unit,
		autoResetToInitialState: !!definition.autoResetToInitialState,
		autoResetStatusName: definition.autoResetStatusName ?? '',
		dismissStatusName: definition.dismissStatusName ?? '',
		identifierGroups: definition.identifierRules?.length
			? groupsFromRules(definition.identifierRules)
			: [[newRow()]],
		alertConditions: conditionsFromApi(definition.alertConditions),
		variables: definition.variables ?? [],
	};
};

const orNull = (value) => (value && String(value).trim() !== '' ? String(value).trim() : null);

export const buildSavePayload = (form) => {
	const isHeartbeat = form.behaviorType === HEARTBEAT_BEHAVIOR;
	const autoResetMinutes = form.autoResetEnabled
		? toAutoResetMinutes(form.autoResetValue, form.autoResetUnit)
		: null;

	return {
		name: form.name.trim(),
		description: form.description.trim(),
		definition: {
			agentName: form.agentName.trim(),
			agentDescription: form.agentDescription.trim(),
			behaviorType: form.behaviorType,
			heartbeatIntervalMinutes: isHeartbeat ? Number(form.heartbeatIntervalMinutes) || DEFAULT_HEARTBEAT_MINUTES : null,
			heartbeatMissedStatusName: isHeartbeat ? orNull(form.heartbeatMissedStatusName) : null,
			autoResetMinutes,
			autoResetToInitialState: autoResetMinutes !== null && form.autoResetToInitialState,
			autoResetStatusName: autoResetMinutes !== null && !form.autoResetToInitialState ? orNull(form.autoResetStatusName) : null,
			dismissStatusName: orNull(form.dismissStatusName),
			identifierRules: rulesFromGroups(form.identifierGroups),
			alertConditions: conditionsToApi(form.alertConditions),
			variables: deriveVariables(form),
		},
	};
};

/** Friendly pre-checks. The API is still the authority; this just avoids an obvious round trip. */
export const validateForm = (form) => {
	const errors = [];
	if (!form.name.trim()) errors.push('Give the template a name.');
	if (!form.agentName.trim()) errors.push('Enter the name each agent will get (it can include {{variables}}).');

	const identifierRules = rulesFromGroups(form.identifierGroups);
	if (identifierRules.length === 0) errors.push('Add at least one identifier rule so emails can be matched to the agent.');

	for (const condition of form.alertConditions) {
		if (!condition.statusName) errors.push('Choose a status for every alert condition.');
		else if (rulesFromGroups(condition.groups).length === 0) errors.push(`Add a rule to the ${condition.statusName} alert condition, or remove it.`);
	}

	if (form.behaviorType === HEARTBEAT_BEHAVIOR) {
		const minutes = Number(form.heartbeatIntervalMinutes);
		if (!Number.isInteger(minutes) || minutes < 1 || minutes > MAX_HEARTBEAT_MINUTES) {
			errors.push(`The heartbeat interval must be between 1 and ${MAX_HEARTBEAT_MINUTES} minutes.`);
		}
	}

	const badTokens = invalidTokens(templateTexts(form));
	if (badTokens.length > 0) {
		errors.push(`${badTokens.join(', ')} is not a valid variable. ${VARIABLE_NAME_HINT}`);
	}

	if (deriveVariables(form).length > 10) errors.push('A template can use at most 10 variables.');
	return [...new Set(errors)];
};

/** Rules with no {{variable}}: the only ones that can be checked against an email before anything is filled in. */
export const countFixedRules = (rules = []) => rules.filter((rule) => !containsToken(rule.value)).length;

/**
 * `text` with each {{variable}} replaced by what has been typed so far. A variable with nothing
 * typed yet stays as its token, so a preview reads "Backup - {{server}}" until it is filled in.
 */
export const previewText = (text, values) => String(text ?? '').replace(tokenPattern(), (token, key) => {
	const entry = Object.entries(values).find(([k]) => k.toLowerCase() === key.toLowerCase());
	const value = (entry?.[1] ?? '').trim();
	return value || token;
});

/**
 * Where a variable is used in a template, in plain words, so the person filling it in can see what
 * their value will be matched against: the agent name/description, and each identifier rule and
 * alert condition that contains it. `values` are the values typed so far (a blank stays as its token).
 */
export const variableUsages = (definition, variableKey, values = {}) => {
	const uses = [];
	const has = (text) => extractVariableKeys([text]).some((k) => k.toLowerCase() === variableKey.toLowerCase());
	if (has(definition?.agentName)) uses.push({ kind: 'name', text: previewText(definition.agentName, values) });
	if (has(definition?.agentDescription)) uses.push({ kind: 'description', text: previewText(definition.agentDescription, values) });
	for (const rule of definition?.identifierRules ?? []) {
		if (has(rule.value)) uses.push({ kind: 'identifier', key: rule.key, matchType: rule.matchType, text: previewText(rule.value, values) });
	}
	for (const rule of definition?.alertConditions ?? []) {
		if (has(rule.value)) uses.push({ kind: 'alert', statusName: rule.statusName, key: rule.key, matchType: rule.matchType, text: previewText(rule.value, values) });
	}
	return uses;
};

/** Card classes for a status, matching the alert-condition cards in the agent editor. */
export const statusCardClass = (statusName) => ({
	critical: 'border-danger bg-danger-subtle',
	severe: 'border-dark bg-dark-subtle',
	warning: 'border-warning bg-warning-subtle',
	clear: 'border-success bg-success-subtle',
	success: 'border-success bg-success-subtle',
	info: 'border-info bg-info-subtle',
	error: 'border-danger bg-danger-subtle',
}[String(statusName ?? '').toLowerCase()] ?? 'border-secondary bg-body-tertiary');

/** Per-variable problems for the create-from-template form, keyed by variable key. */
export const validateVariableValues = (variables, values) => {
	const errors = {};
	for (const variable of variables) {
		const value = (values[variable.key] ?? '').trim();
		if (!value) errors[variable.key] = 'Required';
		else if (value.length > 200) errors[variable.key] = 'At most 200 characters';
	}
	return errors;
};

// ── suggestions (unmatched email that fits a template) ───────────────────────

export const SUGGESTIONS_ENDPOINT = `${TEMPLATES_ENDPOINT}/suggestions`;

// Fired after a suggestion is dismissed or turned into an agent, so the sidebar badge recounts
// straight away instead of waiting for its next poll.
export const SUGGESTIONS_CHANGED_EVENT = 'templateSuggestionsChanged';
export const notifySuggestionsChanged = () => window.dispatchEvent(new Event(SUGGESTIONS_CHANGED_EVENT));

export const MIN_RENDERED_RULE_VALUE_LENGTH = 3;
const MIN_AGENT_NAME_LENGTH = 2;
const MAX_AGENT_NAME_LENGTH = 100;

/**
 * Problems the server would reject once the variables are filled in: an agent name outside 2-100
 * characters, or a rule value built from a variable that ends up under 3 characters (it would match
 * almost every email). Returns a message, or null when the filled-in template is fine.
 */
export const validateRenderedTemplate = (definition, values) => {
	const name = previewText(definition?.agentName, values).trim();
	if (name.length < MIN_AGENT_NAME_LENGTH || name.length > MAX_AGENT_NAME_LENGTH) {
		return `The agent name must be ${MIN_AGENT_NAME_LENGTH} to ${MAX_AGENT_NAME_LENGTH} characters.`;
	}
	const rules = [...(definition?.identifierRules ?? []), ...(definition?.alertConditions ?? [])];
	const tooShort = rules.some((rule) =>
		containsToken(rule.value) && previewText(rule.value, values).trim().length < MIN_RENDERED_RULE_VALUE_LENGTH);
	return tooShort
		? `A rule value is shorter than ${MIN_RENDERED_RULE_VALUE_LENGTH} characters after filling in the variables, which would match too many emails.`
		: null;
};
